import { useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { trySample, type ParagraphRef, type TranslationOrder } from "../api";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";

/**
 * SamplePanel is the two presses that try an instruction on one paragraph
 * (requirement 0044, ADR-0099), and the result they share.
 *
 * **Try it** translates the paragraph the panel is holding, so pressing it after
 * an edit compares two instructions rather than two paragraphs. **Another
 * paragraph** picks a different one and translates it in the same press.
 *
 * Both are paid calls and **both only ever fire from a press** — never on a
 * keystroke, on choosing a preset, or on opening the screen. That rule is what
 * stands in place of an hourly limit (ADR-0095 §3), so it is asserted by an e2e
 * spec counting requests rather than left to the shape of this file.
 *
 * The held paragraph lives here and nowhere else: it is positional and
 * ephemeral, so leaving the screen forgets it, and one the server can no longer
 * resolve comes back as the deterministic paragraph rather than an error.
 */
export default function SamplePanel({
  testID,
  order,
  limits = true,
  children,
  note,
}: {
  /** Prefix for this screen's test ids: `<testID>-try`, `-sample`, and so on. */
  testID: string;
  /** What to translate and how — the same order the run would be started with. */
  order: Omit<TranslationOrder, "linked">;
  /** Whether to say what a sample cannot show. Off where the screen says it already. */
  limits?: boolean;
  /** Any other control belonging in the same row, such as Save. */
  children?: ReactNode;
  /** The line beside the buttons. Defaults to what a press costs. */
  note?: string;
}) {
  const t = useT();
  // Which paragraph is on screen. Null until the first press, which is what
  // makes that press take the first paragraph of the book with enough text.
  const [held, setHeld] = useState<ParagraphRef | null>(null);

  const sample = useMutation({
    mutationFn: (different: boolean) =>
      trySample({ ...order, paragraph: held ?? undefined, different }),
    onSuccess: (result) => setHeld(result.paragraph),
  });
  // Which press is in flight, so only that button says it is working.
  const pending = sample.isPending ? sample.variables : null;

  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid={`${testID}-try`}
          disabled={sample.isPending}
          onClick={() => sample.mutate(false)}
          className="rounded-full border border-line px-4 py-2 text-sm transition-colors hover:bg-sunk disabled:opacity-50"
        >
          {pending === false ? t.translation.trying : t.translation.tryIt}
        </button>
        <button
          type="button"
          data-testid={`${testID}-another`}
          disabled={sample.isPending}
          onClick={() => sample.mutate(true)}
          className="rounded-full border border-line px-4 py-2 text-sm transition-colors hover:bg-sunk disabled:opacity-50"
        >
          {pending === true ? t.translation.trying : t.translation.tryAnother}
        </button>
        {children}
        <span className="text-xs text-muted">{note ?? t.translation.tryNote}</span>
      </div>

      <MutationError
        className="mt-2"
        testId={`${testID}-try-error`}
        error={sample.error}
        fallback={t.translation.tryFailed}
      />

      {sample.data && (
        <div
          data-testid={`${testID}-sample`}
          className="mt-3 rounded-control border border-line bg-sunk p-3 text-sm"
        >
          {/* Authored text in the book's own scripts, source above result: each
              carries its own direction and neither nests inside the other's
              (CLAUDE.md). */}
          <p dir="auto" className="leading-relaxed text-muted">
            {sample.data.source}
          </p>
          <p dir="auto" className="mt-2 leading-relaxed">
            {sample.data.translated}
          </p>
          {/* Two authored runs and a neutral separator between them: an RTL
              chapter title beside an LTR amount would let the "·" and the "$"
              swap ends, so each is isolated (CLAUDE.md §Persian and RTL). */}
          <p className="mt-2 text-xs text-muted">
            <bdi>{sample.data.chapter_title}</bdi>
            {" · "}
            <bdi dir="ltr">{sample.data.cost}</bdi>
          </p>
          {limits && <p className="mt-1 text-xs text-muted">{t.translation.tryLimits}</p>}
        </div>
      )}
    </>
  );
}
